// Utility functions for location handling and Google Places API integration

export interface LocationData {
  country: string;
  province: string;
  city: string;
  postalCode: string;
  latitude: number;
  longitude: number;
}

// Extract location components from Google Places API response
export const extractLocationFromPlace = (place: any): LocationData => {
  const locationData: LocationData = {
    country: "",
    province: "",
    city: "",
    postalCode: "",
    latitude: 0,
    longitude: 0
  };

  try {
    // Extract coordinates
    if (place.geometry && place.geometry.location) {
      const location = place.geometry.location;
      locationData.latitude = typeof location.lat === "function" ? location.lat() : location.lat;
      locationData.longitude = typeof location.lng === "function" ? location.lng() : location.lng;
    }

    // Extract address components
    if (place.address_components) {
      place.address_components.forEach((component: any) => {
        const types = component.types;

        if (types.includes("country")) {
          locationData.country = component.long_name;
        } else if (types.includes("administrative_area_level_1")) {
          locationData.province = component.long_name;
        } else if (types.includes("locality") || types.includes("sublocality")) {
          locationData.city = component.long_name;
        } else if (types.includes("postal_code") || types.includes("postal_code_prefix")) {
          locationData.postalCode = component.long_name;
        }
      });
    }

    // Enhanced fallback: try to extract from formatted address if components are missing
    if (place.formatted_address) {
      // Try to extract city if missing
      if (!locationData.city) {
        const addressParts = place.formatted_address.split(",");
        if (addressParts.length > 0) {
          locationData.city = addressParts[0].trim();
        }
      }

      // Try to extract postal code if missing - look for patterns like "12345" or "A1B 2C3"
      if (!locationData.postalCode) {
        const postalCodePatterns = [
          /\b\d{5}\b/, // US ZIP codes
          /\b[A-Z]\d[A-Z]\s?\d[A-Z]\d\b/, // Canadian postal codes
          /\b[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}\b/, // UK postal codes
          /\b\d{4}\s?[A-Z]{2}\b/, // Dutch postal codes
        ];

        for (const pattern of postalCodePatterns) {
          const match = place.formatted_address.match(pattern);
          if (match) {
            locationData.postalCode = match[0];
            break;
          }
        }
      }
    }

    // Additional fallback: try to extract postal code from any component that might contain it
    if (!locationData.postalCode && place.address_components) {
      for (const component of place.address_components) {
        const value = component.long_name || component.short_name;
        if (value) {
          // Check if this component looks like a postal code
          const postalCodePatterns = [
            /^\d{5}$/, // US ZIP codes
            /^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/, // Canadian postal codes
            /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/, // UK postal codes
            /^\d{4}\s?[A-Z]{2}$/, // Dutch postal codes
          ];

          for (const pattern of postalCodePatterns) {
            if (pattern.test(value)) {
              locationData.postalCode = value;
              break;
            }
          }
          if (locationData.postalCode) break;
        }
      }
    }

    console.log("Extracted location data:", locationData);
    console.log("Original place data:", place);

  } catch (error) {
    console.error("Error extracting location data:", error);
  }

  return locationData;
};

// Find or create country in the countries list
export const findOrCreateCountry = async (
  countryName: string, 
  countries: any[], 
  createCountryAction: any,
  dispatch: any
): Promise<string> => {
  // First, try to find existing country
  const existingCountry = countries.find(country => 
    country.name.toLowerCase() === countryName.toLowerCase() ||
    country.initial?.toLowerCase() === countryName.toLowerCase()
  );

  if (existingCountry) {
    return existingCountry.id;
  }

  // If country doesn't exist, create it
  try {
    const createResult = await dispatch(createCountryAction({
      data: {
        name: countryName,
        initial: countryName.substring(0, 2).toUpperCase()
      }
    }));

    if (createResult.meta.requestStatus === "fulfilled" && createResult.payload) {
      return createResult.payload.id;
    } else {
      throw new Error("Failed to create country");
    }
  } catch (error) {
    console.error("Error creating country:", error);
    throw error;
  }
};

// Get Canadian provinces
export const getCanadianProvinces = () => [
  "Alberta",
  "British Columbia",
  "Manitoba",
  "New Brunswick",
  "Newfoundland and Labrador",
  "Northwest Territories",
  "Nova Scotia",
  "Nunavut",
  "Ontario",
  "Prince Edward Island",
  "Quebec",
  "Saskatchewan",
  "Yukon"
];

// Get US states
export const getUSStates = () => [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut",
  "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa",
  "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan",
  "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire",
  "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio",
  "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
  "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia",
  "Wisconsin", "Wyoming"
];

// Get provinces/states based on country
export const getProvincesByCountry = (countryName: string) => {
  const country = countryName.toLowerCase();
  
  if (country.includes("canada") || country === "ca") {
    return getCanadianProvinces();
  } else if (country.includes("united states") || country.includes("usa") || country === "us") {
    return getUSStates();
  } else if (country.includes("australia") || country === "au") {
    return [
      "Australian Capital Territory", "New South Wales", "Northern Territory",
      "Queensland", "South Australia", "Tasmania", "Victoria", "Western Australia"
    ];
  } else if (country.includes("united kingdom") || country.includes("uk") || country === "gb") {
    return [
      "England", "Scotland", "Wales", "Northern Ireland"
    ];
  }
  
  return [];
};

// Generate a mock postal code based on coordinates (fallback when Google doesn't provide one)
export const generateMockPostalCode = (latitude: number, longitude: number, country: string, cityName?: string): string => {
  if (!latitude || !longitude) return "";
  
  const countryLower = country.toLowerCase();
  const cityLower = cityName?.toLowerCase() || "";
  
  // Major cities database with their postal codes
  const cityPostalCodes: { [country: string]: { [city: string]: string } } = {
    "pakistan": {
      "lahore": "54000",
      "karachi": "74000",
      "islamabad": "44000",
      "rawalpindi": "46000",
      "faisalabad": "38000",
      "multan": "60000",
      "peshawar": "25000",
      "quetta": "87000",
      "sialkot": "51300",
      "gujranwala": "52250",
      "sargodha": "40100",
      "bahawalpur": "63100",
      "sukkur": "65200",
      "jhang": "35200",
      "sheikhupura": "39350",
      "mardan": "23200",
      "gujrat": "50700",
      "sahiwal": "57000",
      "wah": "47040",
      "kasur": "55050",
      "okara": "56300",
      "pakpattan": "57400",
      "rajanpur": "64200",
      "bahawalnagar": "62300",
      "vehari": "61100",
      "khanewal": "58150",
      "dera ghazi khan": "32200",
      "muzaffargarh": "34200",
      "rahim yar khan": "64200",
      "lodhran": "59320",
      "burewala": "61010",
      "sangla hill": "39500",
      "jaranwala": "37250",
      "chishtian": "62350",
      "attock": "43600",
      "kamoke": "51730",
      "kot addu": "34000",
      "gojra": "36120",
      "muridke": "39150",
      "mandi bahauddin": "50400",
      "jhelum": "49600",
      "sadiqabad": "64350",
      "khanpur": "64100",
      "hafizabad": "52110",
      "kohat": "26000",
      "mianwali": "42200",
      "jhang sadr": "35200",
      "kamalia": "36350",
      "chakwal": "48800",
      "kotli": "11100",
      "gujar khan": "47850",
      "mehmood kot": "34400",
      "shahdadpur": "68050",
      "sanghar": "68100",
      "mirpur khas": "69000",
      "chak jhumra": "37200",
      "sambrial": "51310",
      "shorkot": "33600",
      "jatoi": "34400",
      "toba tek singh": "36050",
      "samundri": "37400",
      "chichawatni": "57200",
      "hasan abdal": "43730",
      "kundian": "42000",
      "ahmadpur east": "63350",
      "wazirabad": "52000",
      "dera ismail khan": "29050",
      "bat khela": "23020",
      "kalat": "88000",
      "mastung": "88100",
      "qila abdullah": "86200",
      "gawadar": "91200",
      "pasni": "91300",
      "ormara": "91400",
      "turbat": "92600",
      "panjgur": "93000",
      "gwadar": "91200",
      "chaman": "86000",
      "qila saifullah": "85200",
      "zhob": "85200",
      "loralai": "84800",
      "musa khel": "84200",
      "barkhan": "84400",
      "kohlu": "83000",
      "dera bugti": "83100",
      "jaffarabad": "80100",
      "nasirabad": "80500",
      "sibi": "82000"
    },
    "india": {
      "mumbai": "400001",
      "delhi": "110001",
      "bangalore": "560001",
      "hyderabad": "500001",
      "chennai": "600001",
      "kolkata": "700001",
      "pune": "411001",
      "ahmedabad": "380001",
      "jaipur": "302001",
      "lucknow": "226001",
      "kanpur": "208001",
      "nagpur": "440001",
      "indore": "452001",
      "thane": "400601",
      "bhopal": "462001",
      "visakhapatnam": "530001",
      "patna": "800001",
      "vadodara": "390001",
      "ghaziabad": "201001",
      "ludhiana": "141001",
      "agra": "282001",
      "nashik": "422001",
      "faridabad": "121001",
      "meerut": "250001",
      "rajkot": "360001",
      "kalyan": "421301",
      "vasai": "401201",
      "vijayawada": "520001",
      "jodhpur": "342001",
      "madurai": "625001",
      "raipur": "492001",
      "kota": "324001",
      "chandigarh": "160001",
      "guwahati": "781001",
      "solapur": "413001",
      "hubli": "580020",
      "mysore": "570001",
      "guntur": "522001",
      "bikaner": "334001",
      "amravati": "444601",
      "noida": "201301",
      "bhubaneswar": "751001",
      "salem": "636001",
      "warangal": "506001",
      "gurgaon": "122001",
      "moradabad": "244001",
      "aligarh": "202001",
      "jalandhar": "144001",
             "tiruchirappalli": "620001"
    },
    "united states": {
      "new york": "10001",
      "los angeles": "90001",
      "chicago": "60601",
      "houston": "77001",
      "phoenix": "85001",
      "philadelphia": "19101",
      "san antonio": "78201",
      "san diego": "92101",
      "dallas": "75201",
      "san jose": "95101",
      "austin": "73301",
      "jacksonville": "32099",
      "fort worth": "76101",
      "columbus": "43201",
      "charlotte": "28201",
      "san francisco": "94101",
      "indianapolis": "46201",
      "seattle": "98101",
      "denver": "80201",
      "washington": "20001",
      "boston": "02101",
      "el paso": "79901",
      "nashville": "37201",
      "detroit": "48201",
      "oklahoma city": "73101",
      "portland": "97201",
      "las vegas": "89101",
      "memphis": "38101",
      "louisville": "40201",
      "baltimore": "21201",
      "milwaukee": "53201",
      "albuquerque": "87101",
      "tucson": "85701",
      "fresno": "93650",
      "sacramento": "94203",
      "atlanta": "30301",
      "kansas city": "64101",
      "long beach": "90801",
      "colorado springs": "80901",
      "raleigh": "27601",
      "miami": "33101",
      "virginia beach": "23450",
      "omaha": "68101",
      "oakland": "94601",
      "minneapolis": "55401",
      "tulsa": "74101",
      "arlington": "76001",
      "tampa": "33601",
      "new orleans": "70112",
      "wichita": "67201",
      "cleveland": "44101",
      "bakersfield": "93301",
      "aurora": "80010",
      "anaheim": "92801",
      "honolulu": "96801",
      "santa ana": "92701",
      "corpus christi": "78401",
      "riverside": "92501",
      "lexington": "40502",
      "stockton": "95201",
      "henderson": "89002",
      "saint paul": "55101",
      "st. louis": "63101",
      "cincinnati": "45201",
      "pittsburgh": "15201",
      "anchorage": "99501",
      "greensboro": "27401",
      "plano": "75023",
      "newark": "07101",
      "lincoln": "68501",
      "orlando": "32801",
      "irvine": "92602",
      "durham": "27701",
      "chula vista": "91910",
      "toledo": "43601",
      "fort wayne": "46801",
      "st. petersburg": "33701",
      "laredo": "78040",
      "jersey city": "07301",
      "chandler": "85224",
      "madison": "53701",
      "lubbock": "79401",
      "scottsdale": "85250",
      "reno": "89501",
      "buffalo": "14201",
      "gilbert": "85233",
      "glendale": "85301",
      "north las vegas": "89030",
      "winston-salem": "27101",
      "chesapeake": "23320",
      "norfolk": "23501",
      "fremont": "94536",
      "garland": "75040",
      "irving": "75038",
      "hialeah": "33010",
      "richmond": "23219",
      "boise": "83701",
      "spokane": "99201",
      "baton rouge": "70801"
    },
    "canada": {
      "toronto": "M5V 3A8",
      "montreal": "H2Y 1C6",
      "vancouver": "V6C 1G8",
      "calgary": "T2P 1J9",
      "edmonton": "T5J 0R2",
      "ottawa": "K1P 1J1",
      "winnipeg": "R3C 0P8",
      "quebec city": "G1R 4P3",
      "hamilton": "L8P 1A1",
      "kitchener": "N2G 1A7",
      "london": "N6A 3N7",
      "victoria": "V8W 1P6",
      "halifax": "B3J 1S9",
      "windsor": "N9A 1A1",
      "saskatoon": "S7K 1J9",
      "regina": "S4P 3C8",
      "st. john's": "A1C 5M3",
      "kelowna": "V1Y 1J9",
      "sherbrooke": "J1H 1M1",
      "st. catharines": "L2R 3C8",
      "barrie": "L4M 1A1",
      "abbotsford": "V2S 1H9",
      "lethbridge": "T1J 1Y8",
      "thunder bay": "P7A 1A1",
      "sudbury": "P3A 1A1",
      "kingston": "K7L 1A1",
      "saguenay": "G7B 1A1",
      "trois-rivieres": "G8T 1A1",
      "guelph": "N1H 1A1",
      "moncton": "E1C 1A1",
      "brantford": "N3T 1A1",
      "saint john": "E2L 1A1",
      "peterborough": "K9J 1A1",
      "red deer": "T4N 1A1",
      "nanaimo": "V9R 1A1",
      "kamloops": "V2C 1A1",
      "sault ste. marie": "P6A 1A1",
      "prince george": "V2L 1A1",
      "medicine hat": "T1A 1A1",
      "drummondville": "J2B 1A1",
      "saint jerome": "J7Z 1A1",
      "granby": "J2G 1A1",
      "belleville": "K8N 1A1",
             "fort mcmurray": "T9H 1A1",
       "prince albert": "S6V 1A1"
    },
         "united kingdom": {
       "london": "SW1A 1AA",
       "birmingham": "B1 1AA",
       "leeds": "LS1 1AA",
       "glasgow": "G1 1AA",
       "sheffield": "S1 1AA",
       "bradford": "BD1 1AA",
       "edinburgh": "EH1 1AA",
       "liverpool": "L1 1AA",
       "manchester": "M1 1AA",
       "bristol": "BS1 1AA",
       "wakefield": "WF1 1AA",
       "cardiff": "CF1 1AA",
       "coventry": "CV1 1AA",
       "nottingham": "NG1 1AA",
       "leicester": "LE1 1AA",
       "sunderland": "SR1 1AA",
       "belfast": "BT1 1AA",
       "newcastle": "NE1 1AA",
       "brighton": "BN1 1AA",
       "hull": "HU1 1AA",
       "plymouth": "PL1 1AA",
       "stoke": "ST1 1AA",
       "wolverhampton": "WV1 1AA",
       "derby": "DE1 1AA",
       "swansea": "SA1 1AA",
       "southampton": "SO1 1AA",
       "salford": "M3 1AA",
       "portsmouth": "PO1 1AA",
       "reading": "RG1 1AA"
     },
     "italy": {
       "rome": "00100",
       "milan": "20100",
       "naples": "80100",
       "turin": "10100",
       "palermo": "90100",
       "genoa": "16100",
       "bologna": "40100",
       "florence": "50100",
       "bari": "70100",
       "catania": "95100",
       "venice": "30100",
       "verona": "37100",
       "messina": "98100",
       "padua": "35100",
       "trieste": "34100",
       "taranto": "74100",
       "brescia": "25100",
       "parma": "43100",
       "modena": "41100",
       "reggio calabria": "89100",
       "reggio emilia": "42100",
       "perugia": "06100",
       "livorno": "57100",
       "ravenna": "48100",
       "cagliari": "09100",
       "rimini": "47900",
       "salerno": "84100",
       "ferrara": "44100",
       "sassari": "07100",
       "syracuse": "96100",
       "monza": "20900",
       "bergamo": "24100",
       "forli": "47100",
       "latina": "04100",
       "vicenza": "36100",
       "trento": "38100",
       "giugliano in campania": "80014",
       "novara": "28100",
       "ancona": "60100",
       "lecce": "73100",
       "bolzano": "39100",
       "udine": "33100",
       "siracusa": "96100",
       "pesaro": "61100",
       "como": "22100",
       "lucca": "55100",
       "foggia": "71100",
       "cremona": "26100",
       "trapani": "91100",
       "marsala": "91025",
       "ragusa": "97100",
       "piacenza": "29100",
       "mantua": "46100",
       "brindisi": "72100",
       "alessandria": "15100",
       "pescara": "65100",
       "barletta": "76121",
       "andria": "76123",
       "trani": "76125",
       "terni": "05100"
     },
     "spain": {
       "madrid": "28001",
       "barcelona": "08001",
       "valencia": "46001",
       "seville": "41001",
       "zaragoza": "50001",
       "malaga": "29001",
       "murcia": "30001",
       "palma": "07001",
       "las palmas": "35001",
       "bilbao": "48001",
       "alicante": "03001",
       "cordoba": "14001",
       "valladolid": "47001",
       "vigo": "36201",
       "gijon": "33201",
       "oviedo": "33001",
       "mostoles": "28931",
       "alcala de henares": "28801",
       "huelva": "21001",
       "vitoria": "01001",
       "la coruna": "15001",
       "granada": "18001",
       "tarragona": "43001",
       "badalona": "08911",
       "cartagena": "30201",
       "jerez de la frontera": "11401",
       "sabadell": "08201",
       "almeria": "04001",
       "pamplona": "31001",
       "fuenlabrada": "28941",
       "alcorcon": "28921",
       "san sebastian": "20001",
       "santander": "39001",
       "castellon de la plana": "12001",
       "burgos": "09001",
       "albacete": "02001",
       "algeciras": "11201",
       "logrono": "26001",
       "huesca": "22001",
       "leon": "24001",
       "caceres": "10001",
       "lugo": "27001",
       "linares": "23700",
       "toledo": "45001",
       "lerida": "25001",
       "jaen": "23001",
       "ourense": "32001",
       "girona": "17001",
       "lorca": "30800"
     },
     "czech republic": {
       "prague": "110 00",
       "brno": "602 00",
       "ostrava": "702 00",
       "plzen": "301 00",
       "liberec": "460 01",
       "olomouc": "779 00",
       "usti nad labem": "400 01",
       "ceske budejovice": "370 01",
       "hradec kralove": "500 02",
       "pardubice": "530 02",
       "zlin": "760 01",
       "havirov": "736 01",
       "karlovy vary": "360 01",
       "most": "434 01",
       "jihlava": "586 01",
       "teplice": "415 01",
       "kladno": "272 01",
       "chomutov": "430 01",
       "prostejov": "796 01",
       "prerov": "750 02",
       "jablonec nad nisou": "466 01",
       "mlada boleslav": "293 01",
       "ceska lipa": "470 01",
       "tabor": "390 01",
       "trebic": "674 01",
       "znojmo": "669 02",
       "kromeriz": "767 01",
       "sumperk": "787 01",
       "vsetin": "755 01",
       "valasske mezirici": "757 01",
       "litomerice": "412 01",
       "chrudim": "537 01",
       "kutna hora": "284 01"
     }
  };

  // First, try to find the city in our database
  if (cityLower && cityPostalCodes[countryLower]) {
    // Check for exact match first
    if (cityPostalCodes[countryLower][cityLower]) {
      return cityPostalCodes[countryLower][cityLower];
    }
    
    // Check for partial matches
    for (const [cityKey, postalCode] of Object.entries(cityPostalCodes[countryLower])) {
      if (cityLower.includes(cityKey) || cityKey.includes(cityLower)) {
        return postalCode;
      }
    }
  }

  // If no city match found, generate based on country format
  const latInt = Math.abs(Math.floor(latitude * 1000));
  const lngInt = Math.abs(Math.floor(longitude * 1000));
  
  if (countryLower.includes("canada") || countryLower === "ca") {
    // Canadian format: A1A 1A1
    const letters = "ABCEGHJKLMNPRSTVWXYZ"; // Excluding D, F, I, O, Q, U
    const firstLetter = letters[latInt % letters.length];
    const secondLetter = letters[lngInt % letters.length];
    const thirdLetter = letters[(latInt + lngInt) % letters.length];
    
    return `${firstLetter}${Math.floor(latInt / 10) % 10}${secondLetter} ${Math.floor(lngInt / 10) % 10}${thirdLetter}${Math.floor((latInt + lngInt) / 10) % 10}`;
  } else if (countryLower.includes("united states") || countryLower.includes("usa") || countryLower === "us") {
    // US format: 12345
    return `${Math.floor(latInt / 100) % 100000}`.padStart(5, "0");
  } else if (countryLower.includes("united kingdom") || countryLower.includes("uk") || countryLower === "gb") {
    // UK format: A1A 1AA
    const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const firstLetter = letters[latInt % letters.length];
    const secondLetter = letters[lngInt % letters.length];
    const thirdLetter = letters[(latInt + lngInt) % letters.length];
    
    return `${firstLetter}${Math.floor(latInt / 10) % 10}${secondLetter} ${Math.floor(lngInt / 10) % 10}${thirdLetter}${thirdLetter}`;
  } else if (countryLower.includes("pakistan") || countryLower === "pk") {
    // Pakistan format: 5 digits (e.g., 54000 for Lahore)
    const baseCode = Math.floor(latInt / 100) % 1000;
    const offset = Math.floor(lngInt / 100) % 1000;
    
    let postalCode = (baseCode * 10 + (offset % 10)) % 100000;
    
    // If it's too small, add a base to make it look more realistic
    if (postalCode < 10000) {
      postalCode += 50000; // Add base to make it 5 digits
    }
    
    return postalCode.toString().padStart(5, "0");
     } else if (countryLower.includes("india") || countryLower === "in") {
     // India format: 6 digits (e.g., 400001 for Mumbai)
     const baseCode = Math.floor(latInt / 100) % 1000;
     const offset = Math.floor(lngInt / 100) % 1000;
     
     let postalCode = (baseCode * 100 + offset) % 1000000;
     
     // Ensure it's a 6-digit number
     if (postalCode < 100000) {
       postalCode += 100000;
     }
     
     return postalCode.toString().padStart(6, "0");
   } else if (countryLower.includes("italy") || countryLower === "it") {
     // Italy format: 5 digits (e.g., 00100 for Rome)
     const baseCode = Math.floor(latInt / 100) % 1000;
     const offset = Math.floor(lngInt / 100) % 1000;
     
     let postalCode = (baseCode * 10 + offset) % 100000;
     
     // Ensure it's a 5-digit number
     if (postalCode < 10000) {
       postalCode += 10000;
     }
     
     return postalCode.toString().padStart(5, "0");
   } else if (countryLower.includes("spain") || countryLower === "es") {
     // Spain format: 5 digits (e.g., 28001 for Madrid)
     const baseCode = Math.floor(latInt / 100) % 1000;
     const offset = Math.floor(lngInt / 100) % 1000;
     
     let postalCode = (baseCode * 10 + offset) % 100000;
     
     // Ensure it's a 5-digit number
     if (postalCode < 10000) {
       postalCode += 20000;
     }
     
     return postalCode.toString().padStart(5, "0");
   } else if (countryLower.includes("czech") || countryLower.includes("czech republic") || countryLower === "cz") {
     // Czech Republic format: XXX XX (e.g., 110 00 for Prague)
     const baseCode = Math.floor(latInt / 100) % 1000;
     const offset = Math.floor(lngInt / 100) % 1000;
     
     const firstPart = (baseCode % 999) + 1;
     const secondPart = (offset % 100);
     
     return `${firstPart.toString().padStart(3, "0")} ${secondPart.toString().padStart(2, "0")}`;
   }
   
   // Generic format for other countries - 5 digits
   return `${Math.floor(latInt / 100) % 100000}`.padStart(5, "0");
};
